import {
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Grid,
  Chip,
  LinearProgress,
  Alert,
} from '@mui/material'
import LockResetIcon from '@mui/icons-material/LockReset'
import axios from 'axios'
import PageAppBar from 'components/PageAppBar'
import ProfileDataGrid from 'components/ProfileDataGrid'
import dayjs from 'dayjs'
import { base_url } from 'env'
import { useEffect, useState } from 'react'
import { useLoaderData, useNavigate } from 'react-router'
import { UserModel, ProfileModel, UserSurveyModel, LicenseModel } from 'types'

type UserSubscriptionDetails = {
  subcriptionId: string
  subscriptionName: string
  startDate: number
  endDate: number
  maxProfile: number
  nbOfProfileCreated: number
  maxLicences: number
  nbOfCreatedLicences: number
}

export default function UserPreview() {
  const [profiles, setProfiles] = useState<ProfileModel[]>([])
  const [survey, setSurvey] = useState<UserSurveyModel | null>(null)
  const [subscription, setSubscription] =
    useState<UserSubscriptionDetails | null>(null)
  const [license, setLicense] = useState<LicenseModel | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const result = useLoaderData() as UserModel

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true)
      try {
        const [profilesRes, surveyRes, subscriptionRes, licenseRes] =
          await Promise.allSettled([
            axios.get(`${base_url}/user/${result.login}/profiles`),
            axios.get(`${base_url}/user/${result.login}/survey`),
            axios.get(`${base_url}/user/${result.login}/subscription`),
            axios.get(`${base_url}/user/${result.login}/license`),
          ])

        if (profilesRes.status === 'fulfilled')
          setProfiles(profilesRes.value.data)
        else console.error('Failed to load profiles:', profilesRes.reason)

        if (surveyRes.status === 'fulfilled') setSurvey(surveyRes.value.data)

        if (subscriptionRes.status === 'fulfilled')
          setSubscription(subscriptionRes.value.data)

        if (licenseRes.status === 'fulfilled') setLicense(licenseRes.value.data)
      } catch (error) {
        console.error('An error occurred fetching user data', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchAllData()
  }, [result.login])

  const handleRemoveSubscription = () => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir retirer l'abonnement de cet utilisateur ?`
      )
    )
      return
    axios
      .delete(`${base_url}/user/${result.login}/subscription`)
      .then(() => {
        alert('Abonnement retiré avec succès.')
        setSubscription(null)
      })
      .catch((err) => {
        console.error("La suppression de l'abonnement a échoué:", err)
        alert('La suppression a échoué.')
      })
  }

  const handleDeleteSurvey = () => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer le questionnaire de cet utilisateur ?`
      )
    )
      return
    axios
      .delete(`${base_url}/user/${result.login}/survey`)
      .then(() => {
        alert('Questionnaire supprimé avec succès.')
        setSurvey(null)
      })
      .catch((err) => {
        console.error('La suppression du questionnaire a échoué:', err)
        alert('La suppression a échoué.')
      })
  }

  const handleRemoveLicense = () => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir retirer la licence de cet utilisateur ?`
      )
    )
      return
    axios
      .delete(`${base_url}/user/${result.login}/license`)
      .then(() => {
        alert('Licence retirée avec succès.')
        setLicense(null)
      })
      .catch((err) => {
        console.error('La suppression de la licence a échoué:', err)
        alert('La suppression a échoué.')
      })
  }

  const handleDeleteUser = () => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer cet utilisateur? Cette action est irréversible.`
      )
    )
      return

    axios
      .delete(`${base_url}/user/${result.login}`)
      .then(() => {
        alert('Utilisateur supprimé avec succès.')
        navigate('/users')
      })
      .catch((err) => {
        console.error("La suppression de l'utilisateur a échoué:", err)
        alert('La suppression a échoué.')
      })
  }

  const renderLicense = () => {
    if (isLoading) return null

    if (!license) {
      return (
        <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
          <CardContent>
            <Typography>
              Cet utilisateur n'a pas de licence assignée.
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/user/${result.login}/license/assign`)}
            >
              Assigner une Licence
            </Button>
          </CardActions>
        </Card>
      )
    }

    return (
      <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Licence Assignée
          </Typography>
          <Chip
            label={license.licenseKey}
            sx={{ mb: 1, width: '100%', justifyContent: 'flex-start' }}
          />
          <Chip
            label={license.isMedical ? 'Licence Médicale' : 'Licence Standard'}
            color={license.isMedical ? 'info' : 'default'}
          />
        </CardContent>
        <CardActions>
          <Button
            size="small"
            onClick={() => navigate(`/license/${license.licenseKey}`)}
          >
            Voir les Détails
          </Button>
          <Button size="small" color="error" onClick={handleRemoveLicense}>
            Retirer la Licence
          </Button>
        </CardActions>
      </Card>
    )
  }

  const renderSubscription = () => {
    if (isLoading) return null

    if (!subscription) {
      return (
        <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
          <CardContent>
            <Typography>Cet utilisateur n'a pas d'abonnement actif.</Typography>
          </CardContent>
          <CardActions>
            <Button
              size="small"
              variant="contained"
              onClick={() =>
                navigate(`/user/${result.login}/subscription/assign`)
              }
            >
              Assigner un Abonnement
            </Button>
          </CardActions>
        </Card>
      )
    }

    const profileUsage =
      (subscription.nbOfProfileCreated / subscription.maxProfile) * 100
    const licenseUsage =
      (subscription.nbOfCreatedLicences / subscription.maxLicences) * 100

    return (
      <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Abonnement: {subscription.subscriptionName}
          </Typography>
          <Box sx={{ my: 2 }}>
            <Typography variant="body2">
              Profils Créés: {subscription.nbOfProfileCreated} /{' '}
              {subscription.maxProfile}
            </Typography>
            <LinearProgress variant="determinate" value={profileUsage} />
          </Box>
          <Box>
            <Typography variant="body2">
              Licences Créées: {subscription.nbOfCreatedLicences} /{' '}
              {subscription.maxLicences}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={licenseUsage}
              color="secondary"
            />
          </Box>
        </CardContent>
        <CardActions>
          <Button
            size="small"
            onClick={() =>
              navigate(`/user/${result.login}/subscription/assign`)
            }
          >
            Changer l'Abonnement
          </Button>
          <Button size="small" color="error" onClick={handleRemoveSubscription}>
            Retirer l'Abonnement
          </Button>
        </CardActions>
      </Card>
    )
  }

  const renderSurvey = () => {
    if (isLoading) return null
    if (!survey) {
      return (
        <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
          <CardContent>
            <Typography>
              Cet utilisateur n'a pas encore de questionnaire.
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/user/${result.login}/survey/create`)}
            >
              Créer un questionnaire
            </Button>
          </CardActions>
        </Card>
      )
    }
    return (
      <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Questionnaire
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Chip
                sx={{ width: '100%' }}
                label={`Relation: ${survey.relation_with_profile}`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Chip
                sx={{ width: '100%' }}
                label={`Diplôme: ${survey.highest_diploma}`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Chip
                sx={{ width: '100%' }}
                label={`CSP: ${survey.socio_professional_class}`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Chip
                sx={{ width: '100%' }}
                label={`Fréq. Lecture: ${survey.how_often_they_read}/semaine`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Chip
                sx={{ width: '100%' }}
                label={`Temps Lecture: ${survey.time_spent_reading}h/semaine`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Chip
                sx={{ width: '100%' }}
                label={`Fréq. Suivi: ${survey.how_often_user_monitors_profile_homeworks}/semaine`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Chip
                sx={{ width: '100%' }}
                label={`Temps Suivi: ${survey.time_spent_monitoring}h/semaine`}
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions>
          <Button
            size="small"
            onClick={() => navigate(`/user/${result.login}/survey/edit`)}
          >
            Modifier
          </Button>
          <Button size="small" color="error" onClick={handleDeleteSurvey}>
            Supprimer
          </Button>
        </CardActions>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <PageAppBar pageName="Chargement...">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageAppBar>
    )
  }

  return (
    <PageAppBar pageName={`Utilisateur: ${result.login}`}>
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {result.firstName || 'Utilisateur'} {result.lastName}
          </Typography>
          <Chip label={result.login} color="primary" />
        </Box>

        {result.flagDate && (
          <Alert severity="warning" sx={{ width: '100%', maxWidth: '800px' }}>
            Cet utilisateur est marqué pour suppression le{' '}
            {dayjs(result.flagDate * 1000).format('DD/MM/YYYY')}.
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/user/${result.login}/edit`)}
          >
            Modifier Informations
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<LockResetIcon />}
            onClick={() => navigate(`/user/${result.login}/password/edit`)}
          >
            Changer le mot de passe
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteUser}>
            Supprimer Utilisateur
          </Button>
        </Box>

        <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Détails du Compte
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Chip
                  label={`Email: ${result.email}`}
                  sx={{ width: '100%', justifyContent: 'flex-start', p: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Chip
                  label={`Téléphone: ${result.phone || 'Non renseigné'}`}
                  sx={{ width: '100%', justifyContent: 'flex-start', p: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Chip
                  label={`État: ${result.accountState}`}
                  color={
                    result.accountState === 'active' ? 'success' : 'default'
                  }
                  sx={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Chip
                  label={
                    result.admin ? 'Administrateur' : 'Utilisateur Standard'
                  }
                  color={result.admin ? 'secondary' : 'default'}
                  sx={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Chip
                  label={`Créé le: ${dayjs(Number(result.creationDate) * 1000).format('DD/MM/YYYY HH:mm')}`}
                  sx={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Chip
                  label={`Dernière connexion: ${result.lastConnectionDate ? dayjs(result.lastConnectionDate * 1000).format('DD/MM/YYYY HH:mm') : 'Jamais'}`}
                  sx={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {renderSubscription()}
        {renderLicense()}
        {renderSurvey()}

        <Card sx={{ width: '100%', maxWidth: '1200px' }}>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
              Profiles Accessibles
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <ProfileDataGrid profiles={profiles} />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </PageAppBar>
  )
}
